import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { isCampaignLandingPageReady } from '../optional-setting/common-website-content.mapper';

export interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
}

export interface UtmFieldValidationErrors {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

export interface UtmPlatformRow {
  platformId: string;
  name: string;
  iconType: 'google' | 'facebook' | 'email' | 'sms' | 'mailer';
  url: string;
  isReady: boolean;
  isExpanded: boolean;
  params: UtmParams;
  defaultParams: UtmParams;
  validationErrors: UtmFieldValidationErrors;
}

const EMPTY_UTM_VALIDATION_ERRORS: UtmFieldValidationErrors = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
};

const REQUIRED_UTM_FIELDS: (keyof UtmFieldValidationErrors)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
];

export interface CampaignPlatformApiItem {
  platform: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_page_url?: string | null;
}

const UTM_SAVE_DEBOUNCE_MS = 2000;

const PLATFORM_META: Record<string, { name: string; iconType: UtmPlatformRow['iconType'] }> = {
  google: { name: 'Google Ads', iconType: 'google' },
  facebook: { name: 'Facebook', iconType: 'facebook' },
  email: { name: 'Email', iconType: 'email' },
  sms: { name: 'Text Messaging', iconType: 'sms' },
  mailer: { name: 'Physical Mailer', iconType: 'mailer' },
  physical_mailer: { name: 'Physical Mailer', iconType: 'mailer' },
};

@Component({
  selector: 'app-utm-tagging',
  imports: [CommonModule, FormsModule, CollapsibleSectionComponent],
  templateUrl: './utm-tagging.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './utm-tagging.component.scss',
})
export class UtmTaggingComponent implements OnChanges, OnDestroy {
  @Input() isExpanded = false;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() platformChange = new EventEmitter<CampaignPlatformApiItem[]>();

  platformRows: UtmPlatformRow[] = [];
  readonly utmParamKeys: (keyof UtmParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  private messageService = inject(NotificationService);
  private readonly saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['campaignData']) {
      return;
    }

    const nextPlatformIds = this.getPlatformIdsFromCampaign();
    const currentPlatformIds = this.platformRows.map(row => row.platformId);

    if (
      this.platformRows.length === 0 ||
      !this.areSamePlatformIds(currentPlatformIds, nextPlatformIds)
    ) {
      this.buildPlatformRows();
      return;
    }

    this.syncRowsFromCampaignWithoutRebuild();
  }

  trackByPlatformId(_: number, row: UtmPlatformRow): string {
    return row.platformId;
  }

  ngOnDestroy(): void {
    this.clearAllSaveTimers();
  }

  /** URL text, Copy and Copy Link stay hidden until the landing page exists (CWS-1148). */
  get landingPageReady(): boolean {
    return isCampaignLandingPageReady(this.campaignData);
  }

  get urlCountLabel(): string {
    const count = this.platformRows.length;
    return count === 1 ? '1 URL' : `${count} URLs`;
  }

  onUtmTaggingToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onUtmTaggingDone(): void {
    this.done.emit();
  }

  truncateUrl(url: string, maxLength = 42): string {
    if (url.length <= maxLength) {
      return url;
    }
    return `${url.slice(0, maxLength)}...`;
  }

  toggleRowExpand(row: UtmPlatformRow, event: Event): void {
    event.stopPropagation();
    const willExpand = !row.isExpanded;
    this.platformRows.forEach(r => {
      r.isExpanded = false;
    });
    row.isExpanded = willExpand;
  }

  onParamChange(row: UtmPlatformRow): void {
    this.validateRow(row);
    // Keep the API landing URL's base; only the UTM query changes as the user types.
    row.url = this.buildFullUrl(row.params, row.url?.split('?')[0] || undefined);
    row.isReady = this.isRowReady(row);
    if (row.isReady) {
      this.scheduleDebouncedPlatformSave(row);
    } else {
      this.cancelDebouncedSave(row.platformId);
    }
  }

  validateRow(row: UtmPlatformRow): boolean {
    let isValid = true;

    for (const field of REQUIRED_UTM_FIELDS) {
      if (!row.params[field]?.trim()) {
        row.validationErrors[field] = `${field} is required`;
        isValid = false;
      } else {
        row.validationErrors[field] = '';
      }
    }

    return isValid;
  }

  copyUrl(url: string, event: Event): void {
    event.stopPropagation();
    this.copyToClipboard(url, 'Campaign URL copied to clipboard.');
  }

  copyFullUrl(row: UtmPlatformRow, event: Event): void {
    event.stopPropagation();
    this.copyToClipboard(
      row.url || this.buildFullUrl(row.params),
      'Campaign link copied to clipboard.'
    );
  }

  resetToDefault(row: UtmPlatformRow, event: Event): void {
    event.stopPropagation();
    row.params = { ...row.defaultParams };
    this.onParamChange(row);
  }

  getBaseUrl(): string {
    const slug = this.slugify(this.campaignData?.name || 'black-friday');
    return `https://way.com/lp/${slug}`;
  }

  getUrlCharacterCount(row: UtmPlatformRow): number {
    return (row.url || this.buildFullUrl(row.params)).length;
  }

  getPreviewBaseUrl(row: UtmPlatformRow): string {
    const [base] = (row.url || '').split('?');
    if (base) {
      return `${base}?`;
    }
    return `${this.getBaseUrl()}?`;
  }

  getPreviewParamValue(row: UtmPlatformRow, key: keyof UtmParams): string {
    const valueFromUrl = this.getQueryParamFromUrl(row.url, key);
    if (valueFromUrl !== null) {
      return valueFromUrl;
    }
    return row.params[key] || '';
  }

  isRowReady(row: UtmPlatformRow): boolean {
    return (
      row.params.utm_source.trim() !== '' &&
      row.params.utm_medium.trim() !== '' &&
      row.params.utm_campaign.trim() !== ''
    );
  }

  private copyToClipboard(text: string, detail: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail,
        life: 2000,
      });
    });
  }

  private getPlatformIdsFromCampaign(): string[] {
    return this.extractPlatformEntries().map(entry =>
      typeof entry === 'string' ? entry : entry.platform
    );
  }

  private areSamePlatformIds(currentIds: string[], nextIds: string[]): boolean {
    if (currentIds.length !== nextIds.length) {
      return false;
    }
    const sortedCurrent = [...currentIds].sort();
    const sortedNext = [...nextIds].sort();
    return sortedCurrent.every((id, index) => id === sortedNext[index]);
  }

  /** Keeps in-memory row objects (and input focus) when API echoes UTM saves. */
  private syncRowsFromCampaignWithoutRebuild(): void {
    for (const row of this.platformRows) {
      const apiUrl = this.getLandingPageUrlForPlatform(row.platformId);
      row.url = apiUrl || this.buildFullUrl(row.params);
      row.isReady = this.isRowReady(row);
    }
  }

  private getLandingPageUrlForPlatform(platformId: string): string {
    const entry = this.extractPlatformEntries().find(item => {
      if (typeof item === 'string') {
        return item === platformId;
      }
      return item.platform === platformId;
    });

    if (!entry || typeof entry === 'string') {
      return '';
    }

    return entry.landing_page_url || '';
  }

  private buildPlatformRows(): void {
    const platforms = this.extractPlatformEntries();
    const expandedId = this.platformRows.find(r => r.isExpanded)?.platformId;
    const existingById = new Map(this.platformRows.map(r => [r.platformId, r]));

    this.platformRows = platforms
      .map(entry => {
        const platformId = typeof entry === 'string' ? entry : entry.platform;
        const meta = PLATFORM_META[platformId];
        if (!meta) {
          return null;
        }

        const existing = existingById.get(platformId);
        const apiParams = this.getParamsFromApiEntry(entry);
        const defaultParams =
          existing?.defaultParams ?? apiParams ?? this.getDefaultParams(platformId);
        const params = existing?.params ?? { ...defaultParams };
        const apiUrl = typeof entry === 'string' ? '' : entry.landing_page_url || '';

        const row: UtmPlatformRow = {
          platformId,
          name: meta.name,
          iconType: meta.iconType,
          params,
          defaultParams,
          url: apiUrl || this.buildFullUrl(params),
          isReady: false,
          isExpanded: platformId === expandedId,
          validationErrors: existing?.validationErrors
            ? { ...existing.validationErrors }
            : { ...EMPTY_UTM_VALIDATION_ERRORS },
        };
        this.validateRow(row);
        row.isReady = this.isRowReady(row);
        return row;
      })
      .filter((row): row is UtmPlatformRow => row !== null);
  }

  private extractPlatformEntries(): (string | CampaignPlatformApiItem)[] {
    const source = this.campaignData?.platform ?? [];
    if (!Array.isArray(source)) {
      return [];
    }
    return source
      .map((item: any) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item && typeof item === 'object' && item.platform) {
          return item as CampaignPlatformApiItem;
        }
        return null;
      })
      .filter((item): item is string | CampaignPlatformApiItem => item !== null);
  }

  private getParamsFromApiEntry(entry: string | CampaignPlatformApiItem): UtmParams | null {
    if (typeof entry === 'string') {
      return null;
    }
    return {
      utm_source: this.toInputValue(entry.utm_source),
      utm_medium: this.toInputValue(entry.utm_medium),
      utm_campaign: this.toInputValue(entry.utm_campaign),
      utm_content: this.toInputValue(entry.utm_content),
      utm_term: this.toInputValue(entry.utm_term),
    };
  }

  /** API null/undefined → empty input; preserves non-null strings including empty string. */
  private toInputValue(value: string | null | undefined): string {
    return value == null || value === undefined ? '' : String(value);
  }

  private getDefaultParams(platformId: string): UtmParams {
    const campaignSlug = this.slugify(this.campaignData?.name || 'black-friday');
    const campaign = campaignSlug.replace(/-/g, '_');

    const sourceMap: Record<string, string> = {
      google: 'google',
      facebook: 'facebook',
      email: 'email',
      sms: 'sms',
      mailer: 'mailer',
      physical_mailer: 'physical_mailer',
    };
    const mediumMap: Record<string, string> = {
      google: 'cpc',
      facebook: 'social',
      email: 'email',
      sms: 'sms',
      mailer: 'direct_mail',
      physical_mailer: 'direct_mail',
    };

    return {
      utm_source: sourceMap[platformId] ?? platformId,
      utm_medium: mediumMap[platformId] ?? 'campaign',
      utm_campaign: campaign || 'black_friday_2026',
      utm_content: 'ad_variation_01',
      utm_term: 'car_wash_deals',
    };
  }

  private buildFullUrl(params: UtmParams, base = this.getBaseUrl()): string {
    const search = new URLSearchParams({
      utm_source: params.utm_source.trim(),
      utm_medium: params.utm_medium.trim(),
      utm_campaign: params.utm_campaign.trim(),
      utm_content: params.utm_content.trim(),
      utm_term: params.utm_term.trim(),
    });
    return `${base}?${search.toString()}`;
  }

  private getQueryParamFromUrl(url: string, key: keyof UtmParams): string | null {
    if (!url || !url.includes('?')) {
      return null;
    }
    const query = url.split('?')[1];
    const params = new URLSearchParams(query);
    if (!params.has(key)) {
      return null;
    }
    return params.get(key);
  }

  private slugify(value: string): string {
    return (
      value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'black-friday'
    );
  }

  private scheduleDebouncedPlatformSave(row: UtmPlatformRow): void {
    if (!this.isRowReady(row)) {
      return;
    }

    const existing = this.saveTimers.get(row.platformId);
    if (existing) {
      clearTimeout(existing);
    }

    const timer = setTimeout(() => {
      this.saveTimers.delete(row.platformId);
      this.emitPlatformUpdate();
    }, UTM_SAVE_DEBOUNCE_MS);

    this.saveTimers.set(row.platformId, timer);
  }

  private emitPlatformUpdate(): void {
    this.platformChange.emit(this.buildPlatformsForApi());
  }

  /** UTM fields only — landing_page_url is read-only from API and must not be sent on update. */
  private pickUtmFieldsForApi(entry: CampaignPlatformApiItem): CampaignPlatformApiItem {
    return {
      platform: entry.platform,
      utm_source: entry.utm_source ?? null,
      utm_medium: entry.utm_medium ?? null,
      utm_campaign: entry.utm_campaign ?? null,
      utm_content: entry.utm_content ?? null,
      utm_term: entry.utm_term ?? null,
    };
  }

  private buildPlatformsForApi(): CampaignPlatformApiItem[] {
    return this.extractPlatformEntries().map(entry => {
      const platformId = typeof entry === 'string' ? entry : entry.platform;
      const row = this.platformRows.find(r => r.platformId === platformId);

      if (!row || !this.isRowReady(row)) {
        if (typeof entry === 'string') {
          return { platform: platformId };
        }
        return this.pickUtmFieldsForApi(entry);
      }

      return {
        platform: platformId,
        utm_source: row.params.utm_source.trim(),
        utm_medium: row.params.utm_medium.trim(),
        utm_campaign: row.params.utm_campaign.trim(),
        utm_content: row.params.utm_content.trim() || null,
        utm_term: row.params.utm_term.trim() || null,
      };
    });
  }

  private cancelDebouncedSave(platformId: string): void {
    const existing = this.saveTimers.get(platformId);
    if (existing) {
      clearTimeout(existing);
      this.saveTimers.delete(platformId);
    }
  }

  private clearAllSaveTimers(): void {
    this.saveTimers.forEach(timer => clearTimeout(timer));
    this.saveTimers.clear();
  }
}
